/**
 * Newsletter Editor Page (Spec §1.6 + §1.5).
 *
 * Inline edit + live HTML preview side-by-side.  CEO/leader only.
 * - Section + item editors with add/remove/reorder.
 * - Preview iframe pulls from `/api/newsletters/{id}/preview`
 *   (admin endpoint; renders with the "DRAFT" ribbon).
 * - "Personalise for member" lets Dipali toggle to a Soul-Form-aware preview
 *   so she can see exactly what a specific JLR member will receive.
 * - "Approve" flips status -> ready_to_send; subsequent edits revert it
 *   to draft for safety.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { canAccessClientIntel } from '@/lib/permissions';
import { copyNewsletterHtml, downloadNewsletterHtml } from '@/lib/newsletterHtml';
import {
  ChevronLeft, Save, CheckCircle2, RotateCcw, Plus, Trash2,
  ArrowUp, ArrowDown, Eye, ExternalLink, User, Sparkles,
  Wand2, Send, Copy, Code, Download,
} from 'lucide-react';
import NewsletterImagePicker from '@/components/NewsletterImagePicker';
import NewsletterDispatchModal from '@/components/NewsletterDispatchModal';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const blankItem = () => ({
  title: '', description: '', concierge_addon: '',
  link_text: 'Reserve', link_url: '', image: '',
});

const blankSection = () => ({ title: '', intro: '', items: [blankItem()] });

export default function NewsletterEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCeo = user?.role === 'ceo';
  // Concierge® team members (Nisha, Samar, Shiva, Tanisha) can also
  // dispatch newsletters — Tanisha is the designated operator.
  const canDispatch = canAccessClientIntel(user);
  const isLeader = ['ceo','leader','sub_leader'].includes((user?.role || '').toLowerCase());

  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [members, setMembers] = useState([]);
  const [previewMember, setPreviewMember] = useState('');
  const [previewKey, setPreviewKey] = useState(0);
  const previewRef = useRef(null);
  const [autoFilling, setAutoFilling] = useState(false);
  const [autoFillResult, setAutoFillResult] = useState(null);
  const [showDispatch, setShowDispatch] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/newsletters/${id}`);
      setDoc(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load');
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  // Optional: lazy-load a small set of soul-form-completed members for the
  // "personalise preview" dropdown.  Errors are non-fatal (graceful fallback).
  useEffect(() => {
    if (!doc?.client_id) return;
    api.get(`/soul/members?client_id=${doc.client_id}&limit=20&completed=1`)
      .then(r => setMembers(r.data?.members || []))
      .catch(() => setMembers([]));
  }, [doc?.client_id]);

  if (!isLeader) {
    return <div className="p-6 text-sm" style={{ color: '#A32D2D' }}>
      Newsletter editor is for CEO + leaders only.
    </div>;
  }

  if (error) return <div className="p-6 text-sm" style={{ color: '#A32D2D' }}>{error}</div>;
  if (!doc) return <div className="p-6 text-sm" style={{ color: '#9CA3AF' }}>Loading…</div>;

  const reloadPreview = () => setPreviewKey(k => k + 1);

  const saveAll = async (overrideDoc) => {
    const payloadDoc = overrideDoc || doc;
    setSaving(true);
    try {
      const payload = {
        subject: payloadDoc.subject,
        headline: payloadDoc.headline,
        intro_paragraph: payloadDoc.intro_paragraph,
        closing_paragraph: payloadDoc.closing_paragraph,
        sections: payloadDoc.sections || [],
        reserve_block: payloadDoc.reserve_block,
        quick_replies: payloadDoc.quick_replies,
        personal_opening_enabled: !!payloadDoc.personal_opening_enabled,
        explore_url: payloadDoc.explore_url,
        // Branding overrides — sent only when user has set something.
        // Empty strings unset → renderer falls back to smart defaults.
        brand_name: payloadDoc.brand_name || '',
        brand_tagline: payloadDoc.brand_tagline || '',
        footer_tagline: payloadDoc.footer_tagline || '',
        privacy_url: payloadDoc.privacy_url || '',
        terms_url: payloadDoc.terms_url || '',
      };
      await api.put(`/newsletters/${id}`, payload);
      setSavedAt(new Date());
      // Refetch to pick up status auto-revert if applicable
      await refresh();
      reloadPreview();
    } catch (e) {
      alert(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    await saveAll();
    try {
      const r = await api.post(`/newsletters/${id}/approve`);
      setDoc(d => ({ ...d, status: r.data.status, approved_at: r.data.approved_at }));
    } catch (e) {
      alert(`Approve failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const revert = async () => {
    try {
      const r = await api.post(`/newsletters/${id}/revert-to-draft`);
      setDoc(d => ({ ...d, status: r.data.status, approved_at: null }));
    } catch (e) {
      alert(`Revert failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // Re-open a `sent` newsletter for editing + correction-resend.
  // Confirmation dialog is intentional: this is a high-impact action
  // (the next dispatch is flagged as a CORRECTION to Tanisha, who'll
  // know to discard the previous file). No time guardrail — Dipali
  // wants instant re-open even days after the original send.
  const reopen = async () => {
    const ok = window.confirm(
      'Re-open this newsletter for editing?\n\n' +
      '• Status will move from SENT back to DRAFT.\n' +
      '• You can edit the content.\n' +
      '• When you Send again, Tanisha gets a CORRECTION email that ' +
      'supersedes the previous handover.\n' +
      '• Earlier dispatch is preserved in the audit trail.\n\n' +
      'Continue?'
    );
    if (!ok) return;
    try {
      const r = await api.post(`/newsletters/${id}/reopen`);
      setDoc(d => ({
        ...d, status: r.data.status,
        approved_at: null, dispatched_at: null,
        reopened_after_send_at: r.data.reopened_after_send_at,
        dispatch_history_count: r.data.dispatch_history_count,
      }));
      await refresh();
    } catch (e) {
      alert(`Re-open failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const duplicateNewsletter = async () => {
    const nextMonth = doc.month === 12 ? 1 : doc.month + 1;
    const nextYear = doc.month === 12 ? doc.year + 1 : doc.year;
    const monthName = ['January','February','March','April','May','June','July','August','September','October','November','December'][nextMonth - 1];
    const ok = window.confirm(
      `Clone this newsletter to ${monthName} ${nextYear}?\n\n` +
      `All content + images will be copied.\nYou'll then edit the words for ${monthName}.`
    );
    if (!ok) return;
    try {
      const r = await api.post(`/newsletters/${id}/duplicate`, {
        month: nextMonth, year: nextYear, keep_images: true,
      });
      const newId = r.data.newsletter.id;
      navigate(`/newsletters/${newId}`);
    } catch (e) {
      alert(`Duplicate failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const autoFillImages = async (overwrite = false) => {
    const emptyCount = (doc.sections || []).reduce(
      (n, s) => n + (s.items || []).filter(i => !(i.image || '').trim()).length, 0,
    );
    const totalCount = (doc.sections || []).reduce(
      (n, s) => n + (s.items || []).length, 0,
    );
    const target = overwrite ? totalCount : emptyCount;
    if (target === 0) {
      alert('Every item already has an image. Use "Replace all" if you want to refresh them.');
      return;
    }
    const ok = window.confirm(
      `Auto-fill ${target} image${target > 1 ? 's' : ''} via web search? ` +
      `(Each item gets the top Tavily result imported to Cloudinary.)`
    );
    if (!ok) return;
    setAutoFilling(true);
    setAutoFillResult(null);
    try {
      const r = await api.post(`/newsletters/${id}/auto-fill-images`,
        { overwrite, max_items: 30 },
        { timeout: 240000 });
      setAutoFillResult(r.data);
      await refresh();
      reloadPreview();
    } catch (e) {
      alert(`Auto-fill failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setAutoFilling(false);
    }
  };

  // ---- Section + item helpers (immutable updates) ----
  const updateSection = (idx, patch) =>
    setDoc(d => ({ ...d, sections: d.sections.map((s, i) => i === idx ? { ...s, ...patch } : s) }));

  const updateItem = (sIdx, iIdx, patch) =>
    setDoc(d => ({ ...d, sections: d.sections.map((s, i) =>
      i === sIdx
        ? { ...s, items: s.items.map((it, j) => j === iIdx ? { ...it, ...patch } : it) }
        : s) }));

  const addSection = () =>
    setDoc(d => ({ ...d, sections: [...(d.sections || []), blankSection()] }));

  const removeSection = (idx) => {
    if (!window.confirm('Remove this section?')) return;
    setDoc(d => ({ ...d, sections: d.sections.filter((_, i) => i !== idx) }));
  };

  const moveSection = (idx, dir) =>
    setDoc(d => {
      const target = idx + dir;
      if (target < 0 || target >= d.sections.length) return d;
      const next = [...d.sections];
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...d, sections: next };
    });

  const addItem = (sIdx) =>
    setDoc(d => ({ ...d, sections: d.sections.map((s, i) =>
      i === sIdx ? { ...s, items: [...(s.items || []), blankItem()] } : s) }));

  const removeItem = (sIdx, iIdx) =>
    setDoc(d => ({ ...d, sections: d.sections.map((s, i) =>
      i === sIdx ? { ...s, items: s.items.filter((_, j) => j !== iIdx) } : s) }));

  const moveItem = (sIdx, iIdx, dir) =>
    setDoc(d => ({ ...d, sections: d.sections.map((s, i) => {
      if (i !== sIdx) return s;
      const target = iIdx + dir;
      if (target < 0 || target >= s.items.length) return s;
      const items = [...s.items];
      [items[iIdx], items[target]] = [items[target], items[iIdx]];
      return { ...s, items };
    }) }));

  // ---- Preview URL ----
  const previewSrc = (() => {
    const base = `/api/newsletters/${id}/preview?cache=${previewKey}`;
    return previewMember ? `${base}&member_id=${encodeURIComponent(previewMember)}` : base;
  })();

  const status = doc.status || 'draft';
  const statusColors = {
    draft: { bg: '#FEF3C7', fg: '#92400E', label: 'Draft' },
    ready_to_send: { bg: '#DCFCE7', fg: '#166534', label: 'Ready to Send' },
    sent: { bg: '#DBEAFE', fg: '#1E3A8A', label: 'Sent' },
  }[status] || { bg: '#FEF3C7', fg: '#92400E', label: status };

  return (
    <div className="space-y-4 fade-in" data-testid="newsletter-editor-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(`/client-intelligence/${doc.client_id}`)}
            className="flex items-center gap-1 text-sm hover:opacity-70"
            style={{ color: '#2A7B88' }} data-testid="newsletter-back-btn">
            <ChevronLeft size={16} /> Back to {doc.client_id?.toUpperCase()}
          </button>
          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium"
            style={{ background: statusColors.bg, color: statusColors.fg }}
            data-testid="newsletter-status-pill">
            {statusColors.label}
          </span>
          {doc.theme && (
            <span className="text-[11px] px-2 py-0.5 rounded-full"
              style={{ background: '#F7F5F2', color: doc.theme.accent }}>
              {doc.theme.label} · {doc.theme.accent}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {savedAt && (
            <span className="text-[11px]" style={{ color: '#6B7280' }}>
              Saved {savedAt.toLocaleTimeString()}
            </span>
          )}
          <button onClick={duplicateNewsletter}
            title="Clone this newsletter to next month"
            className="px-3 py-1.5 rounded-md text-[12px] font-medium border hover:bg-gray-50 inline-flex items-center gap-1"
            style={{ borderColor: '#1B2A4A', color: '#1B2A4A' }}
            data-testid="newsletter-duplicate-btn">
            <Copy size={12} /> Duplicate to next month
          </button>
          <button onClick={() => autoFillImages(false)} disabled={autoFilling || saving}
            title="Auto-fill missing images via Tavily web search"
            className="px-3 py-1.5 rounded-md text-[12px] font-medium border hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-60"
            style={{ borderColor: '#7A5C2B', color: '#7A5C2B' }}
            data-testid="newsletter-autofill-btn">
            <Wand2 size={12} /> {autoFilling ? 'Filling images…' : 'Auto-fill images'}
          </button>
          <button
            onClick={() => copyNewsletterHtml(id)}
            title="Copy the bulk-send HTML to clipboard (paste into Zoho Campaigns)"
            className="px-3 py-1.5 rounded-md text-[12px] font-medium border hover:bg-gray-50 inline-flex items-center gap-1"
            style={{ borderColor: '#1B2A4A', color: '#1B2A4A' }}
            data-testid="newsletter-copy-html-btn">
            <Code size={12} /> Copy HTML
          </button>
          <button
            onClick={() => {
              const fname = `${(doc.client_id || 'newsletter').toLowerCase()}-${doc.year}-${String(doc.month).padStart(2,'0')}-newsletter.html`;
              return downloadNewsletterHtml(id, fname);
            }}
            title="Download the bulk-send HTML as a .html file"
            className="px-3 py-1.5 rounded-md text-[12px] font-medium border hover:bg-gray-50 inline-flex items-center gap-1"
            style={{ borderColor: '#1B2A4A', color: '#1B2A4A' }}
            data-testid="newsletter-download-html-btn">
            <Download size={12} /> Download .html
          </button>
          <button onClick={() => saveAll()} disabled={saving}
            className="px-3 py-1.5 rounded-md text-[12px] font-medium border hover:bg-gray-50 inline-flex items-center gap-1"
            style={{ borderColor: '#1C304A', color: '#1B2A4A' }}
            data-testid="newsletter-save-btn">
            <Save size={12} /> {saving ? 'Saving…' : 'Save'}
          </button>
          {status === 'draft' && (
            <button onClick={approve}
              className="px-3 py-1.5 rounded-md text-[12px] font-medium text-white inline-flex items-center gap-1"
              style={{ backgroundColor: '#16653A' }}
              data-testid="newsletter-approve-btn">
              <CheckCircle2 size={12} /> Approve
            </button>
          )}
          {canDispatch && (
            <button onClick={() => setShowDispatch(true)}
              className="px-3 py-1.5 rounded-md text-[12px] font-medium text-white inline-flex items-center gap-1"
              style={{ backgroundColor: '#16653A' }}
              data-testid="newsletter-dispatch-open">
              <Send size={12} /> Send
            </button>
          )}
          {status === 'ready_to_send' && (
            <button onClick={revert}
              className="px-3 py-1.5 rounded-md text-[12px] font-medium border hover:bg-gray-50 inline-flex items-center gap-1"
              style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
              data-testid="newsletter-revert-btn">
              <RotateCcw size={12} /> Revert to draft
            </button>
          )}
          {status === 'sent' && (
            <>
              <button onClick={reopen}
                title="Re-open this sent newsletter to fix a mistake (Tanisha gets a CORRECTION email)"
                className="px-3 py-1.5 rounded-md text-[12px] font-medium border hover:bg-amber-50 inline-flex items-center gap-1"
                style={{ borderColor: '#A16207', color: '#A16207' }}
                data-testid="newsletter-reopen-btn">
                <RotateCcw size={12} /> Reopen & edit
              </button>
              <span className="px-3 py-1.5 rounded-md text-[12px] font-medium inline-flex items-center gap-1"
                style={{ background: '#DBEAFE', color: '#1E3A8A' }}
                data-testid="newsletter-sent-badge">
                <Send size={12} /> Sent
              </span>
            </>
          )}
          <a href={`/c/${doc.public_token}`} target="_blank" rel="noreferrer"
            className="px-3 py-1.5 rounded-md text-[12px] font-medium border hover:bg-gray-50 inline-flex items-center gap-1"
            style={{ borderColor: '#7A5C2B', color: '#7A5C2B' }}
            data-testid="newsletter-public-link">
            <ExternalLink size={12} /> Public link
          </a>
        </div>
      </div>

      {doc.reopened_after_send_at && status !== 'sent' && (
        <div
          className="rounded-md border px-3 py-2.5 text-[12.5px] flex items-start gap-2"
          style={{ borderColor: '#A16207', background: '#FFFBEB', color: '#7A4F08' }}
          data-testid="newsletter-correction-banner">
          <span style={{ fontSize: '16px', lineHeight: '1.4' }}>⚠️</span>
          <div className="leading-relaxed">
            <strong>Correction mode.</strong> This newsletter was previously sent on{' '}
            {new Date(doc.reopened_after_send_at).toLocaleString('en-IN', {
              day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
            })}{' '}
            and re-opened for editing.
            {doc.dispatch_history_count > 0 && ` ${doc.dispatch_history_count} earlier dispatch${doc.dispatch_history_count > 1 ? 'es' : ''} archived.`}
            {' '}When you Send next, Tanisha will receive a <strong>CORRECTION</strong> email
            that supersedes the previous handover.
          </div>
        </div>
      )}

      {autoFillResult && (
        <div className="rounded-md border px-3 py-2 text-[12px] flex items-start justify-between gap-3"
          style={{ borderColor: '#E2D8C2', background: '#FFF8EC', color: '#5C4F3F' }}
          data-testid="newsletter-autofill-result">
          <div>
            <strong>Auto-fill done.</strong>{' '}
            {autoFillResult.filled} filled
            {autoFillResult.failed > 0 && <span style={{ color: '#A32D2D' }}> · {autoFillResult.failed} failed</span>}
            {autoFillResult.skipped > 0 && <span style={{ color: '#6B7280' }}> · {autoFillResult.skipped} skipped (had image)</span>}.
            {autoFillResult.failed > 0 && (
              <span> Open the failed items below to add an image manually or retry.</span>
            )}
          </div>
          <button onClick={() => setAutoFillResult(null)}
            className="text-[11px] px-2 py-0.5 rounded border"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
            Dismiss
          </button>
        </div>
      )}

      {/* Section thumbnail strip — quick-jump table of contents */}
      {(doc.sections || []).length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="newsletter-toc-strip">
          {doc.sections.map((s, idx) => {
            const firstImage = (s.items || []).find(it => (it.image || '').trim())?.image;
            return (
              <button key={idx}
                onClick={() => {
                  const el = document.querySelector(`[data-testid="newsletter-section-${idx}"]`);
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                title={s.title}
                className="flex-shrink-0 w-32 rounded-md border overflow-hidden hover:shadow-md transition text-left"
                style={{ borderColor: '#E5E1DC' }}
                data-testid={`newsletter-toc-item-${idx}`}>
                <div className="h-14 w-full bg-gray-100" style={{
                  backgroundImage: firstImage ? `url(${firstImage})` : 'linear-gradient(135deg, #EFE7D5, #DCC9A5)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}>
                  {!firstImage && (
                    <div className="h-full flex items-center justify-center text-[9px] tracking-widest uppercase" style={{ color: '#7A5C2B' }}>
                      No image
                    </div>
                  )}
                </div>
                <div className="px-2 py-1 text-[10px] truncate" style={{ color: '#1B2A4A' }}>
                  {s.title}
                </div>
                <div className="px-2 pb-1 text-[9px]" style={{ color: '#9CA3AF' }}>
                  {s.items?.length || 0} {(s.items?.length || 0) === 1 ? 'item' : 'items'}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Two-column: editor (left) + preview (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ----- LEFT: editor ----- */}
        <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-2" data-testid="newsletter-editor-pane">
          <div className="bg-white rounded-xl border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }}>
            <div className="text-[11px] uppercase tracking-wider" style={{ color: '#6B7280' }}>
              {MONTH_NAMES[doc.month - 1]} {doc.year} · {doc.client_id?.toUpperCase()}
            </div>
            <FieldText label="Subject (email)" value={doc.subject || ''}
              onChange={(v) => setDoc(d => ({ ...d, subject: v }))}
              testId="newsletter-edit-subject" />
            <FieldText label="Headline (newsletter title)" value={doc.headline || ''}
              onChange={(v) => setDoc(d => ({ ...d, headline: v }))}
              testId="newsletter-edit-headline" />
            <FieldTextarea label="Intro paragraph" value={doc.intro_paragraph || ''}
              onChange={(v) => setDoc(d => ({ ...d, intro_paragraph: v }))}
              rows={3} testId="newsletter-edit-intro" />
          </div>

          {(doc.sections || []).map((section, sIdx) => (
            <div key={sIdx} className="bg-white rounded-xl border p-4 space-y-3"
              style={{ borderColor: '#E5E1DC' }} data-testid={`newsletter-section-${sIdx}`}>
              <div className="flex items-start justify-between gap-2">
                <FieldText label={`Section ${sIdx + 1} title`} value={section.title}
                  onChange={(v) => updateSection(sIdx, { title: v })}
                  testId={`newsletter-section-title-${sIdx}`} />
                <div className="flex gap-1 mt-5">
                  <IconBtn onClick={() => moveSection(sIdx, -1)} title="Move up" testId={`newsletter-section-up-${sIdx}`}>
                    <ArrowUp size={12} />
                  </IconBtn>
                  <IconBtn onClick={() => moveSection(sIdx, 1)} title="Move down" testId={`newsletter-section-down-${sIdx}`}>
                    <ArrowDown size={12} />
                  </IconBtn>
                  <IconBtn onClick={() => removeSection(sIdx)} title="Remove" danger testId={`newsletter-section-del-${sIdx}`}>
                    <Trash2 size={12} />
                  </IconBtn>
                </div>
              </div>
              <FieldTextarea label="Section intro (optional)" value={section.intro || ''}
                onChange={(v) => updateSection(sIdx, { intro: v })}
                rows={2} testId={`newsletter-section-intro-${sIdx}`} />

              <div className="space-y-3 pl-2 border-l-2" style={{ borderColor: '#F0EDE7' }}>
                {(section.items || []).map((item, iIdx) => (
                  <div key={iIdx} className="space-y-2 pb-3 border-b" style={{ borderColor: '#F7F5F2' }}
                    data-testid={`newsletter-item-${sIdx}-${iIdx}`}>
                    <div className="flex items-start justify-between gap-2">
                      <FieldText label={`Item ${iIdx + 1} title`} value={item.title}
                        onChange={(v) => updateItem(sIdx, iIdx, { title: v })}
                        testId={`newsletter-item-title-${sIdx}-${iIdx}`} />
                      <div className="flex gap-1 mt-5">
                        <IconBtn onClick={() => moveItem(sIdx, iIdx, -1)} title="Move up" testId={`newsletter-item-up-${sIdx}-${iIdx}`}>
                          <ArrowUp size={11} />
                        </IconBtn>
                        <IconBtn onClick={() => moveItem(sIdx, iIdx, 1)} title="Move down" testId={`newsletter-item-down-${sIdx}-${iIdx}`}>
                          <ArrowDown size={11} />
                        </IconBtn>
                        <IconBtn onClick={() => removeItem(sIdx, iIdx)} title="Remove" danger testId={`newsletter-item-del-${sIdx}-${iIdx}`}>
                          <Trash2 size={11} />
                        </IconBtn>
                      </div>
                    </div>
                    <FieldTextarea label="Description" value={item.description}
                      onChange={(v) => updateItem(sIdx, iIdx, { description: v })}
                      rows={2} testId={`newsletter-item-desc-${sIdx}-${iIdx}`} />
                    <FieldText label='"Your Concierge® can …" addon'
                      value={item.concierge_addon}
                      placeholder="hold a sea-facing villa with a private chef…"
                      onChange={(v) => updateItem(sIdx, iIdx, { concierge_addon: v })}
                      testId={`newsletter-item-addon-${sIdx}-${iIdx}`} />
                    <div className="grid grid-cols-2 gap-2">
                      <FieldText label="Link text" value={item.link_text}
                        onChange={(v) => updateItem(sIdx, iIdx, { link_text: v })}
                        testId={`newsletter-item-linktext-${sIdx}-${iIdx}`} />
                      <FieldText label="Link URL (optional)" value={item.link_url}
                        onChange={(v) => updateItem(sIdx, iIdx, { link_url: v })}
                        testId={`newsletter-item-linkurl-${sIdx}-${iIdx}`} />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>Image</label>
                      <NewsletterImagePicker
                        value={item.image}
                        onChange={(url) => updateItem(sIdx, iIdx, { image: url })}
                        clientId={doc.client_id}
                        year={doc.year}
                        month={doc.month}
                        itemTitle={item.title}
                        itemDescription={item.description}
                        sectionTitle={section.title}
                        testIdPrefix={`newsletter-item-${sIdx}-${iIdx}`}
                      />
                    </div>
                  </div>
                ))}
                <button onClick={() => addItem(sIdx)}
                  className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border hover:bg-gray-50"
                  style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                  data-testid={`newsletter-add-item-${sIdx}`}>
                  <Plus size={11} /> Add item
                </button>
              </div>
            </div>
          ))}

          <button onClick={addSection}
            className="w-full px-3 py-2 rounded-md text-[12px] font-medium border-2 border-dashed hover:bg-gray-50 inline-flex items-center justify-center gap-1"
            style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
            data-testid="newsletter-add-section">
            <Plus size={12} /> Add section
          </button>

          <div className="bg-white rounded-xl border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }}>
            <FieldTextarea label="Closing paragraph" value={doc.closing_paragraph || ''}
              onChange={(v) => setDoc(d => ({ ...d, closing_paragraph: v }))}
              rows={3} testId="newsletter-edit-closing" />
            <label className="text-[12px] flex items-center gap-2" style={{ color: '#1B2A4A' }}>
              <input type="checkbox" checked={!!doc.personal_opening_enabled}
                onChange={(e) => setDoc(d => ({ ...d, personal_opening_enabled: e.target.checked }))}
                data-testid="newsletter-personal-opening-toggle" />
              <Sparkles size={12} style={{ color: '#7A5C2B' }} /> Enable personal opening line for Soul-Form members
              <span className="text-[10px]" style={{ color: '#9CA3AF' }}>(Spec §1.5 Level 2)</span>
            </label>
          </div>

          {/* Branding & Footer (advanced) — header brand, tagline,
              footer tagline, privacy/terms links. All optional;
              blank ⇒ smart defaults. */}
          <details
            className="bg-white rounded-xl border p-4 group"
            style={{ borderColor: '#E5E1DC' }}
            data-testid="newsletter-branding-panel">
            <summary
              className="text-[12px] font-semibold cursor-pointer flex items-center gap-2 select-none"
              style={{ color: '#1B2A4A' }}>
              <Sparkles size={12} style={{ color: '#7A5C2B' }} />
              Branding & footer (advanced)
              <span className="text-[10px] font-normal" style={{ color: '#9CA3AF' }}>
                Header name · tagline · footer · Privacy/Terms links
              </span>
            </summary>
            <div className="space-y-3 mt-3 pt-3 border-t" style={{ borderColor: '#F0EDE7' }}>
              <FieldText
                label='Header brand name (HTML allowed — e.g. "concierge<sup>®</sup>")'
                value={doc.brand_name || ''}
                onChange={(v) => setDoc(d => ({ ...d, brand_name: v }))}
                placeholder="concierge®"
                testId="newsletter-edit-brand-name" />
              <FieldText
                label="Header tagline (under the brand name)"
                value={doc.brand_tagline || ''}
                onChange={(v) => setDoc(d => ({ ...d, brand_tagline: v }))}
                placeholder={`Curated for ${doc.theme?.label || (doc.client_id || '').toUpperCase()}`}
                testId="newsletter-edit-brand-tagline" />
              <FieldText
                label="Footer tagline (above Explore/Chat/Call buttons)"
                value={doc.footer_tagline || ''}
                onChange={(v) => setDoc(d => ({ ...d, footer_tagline: v }))}
                placeholder="Concierge® — Your World. Unlocked."
                testId="newsletter-edit-footer-tagline" />
              <div className="grid grid-cols-2 gap-3">
                <FieldText
                  label="Privacy URL"
                  value={doc.privacy_url || ''}
                  onChange={(v) => setDoc(d => ({ ...d, privacy_url: v }))}
                  placeholder="https://app.concierges.in/privacy"
                  testId="newsletter-edit-privacy-url" />
                <FieldText
                  label="Terms URL"
                  value={doc.terms_url || ''}
                  onChange={(v) => setDoc(d => ({ ...d, terms_url: v }))}
                  placeholder="https://app.concierges.in/terms"
                  testId="newsletter-edit-terms-url" />
              </div>
              <p className="text-[10.5px]" style={{ color: '#9CA3AF' }}>
                Leave any field blank to use the smart default. The Unsubscribe link
                always points to <code>/u/{doc.public_token}</code> for compliance —
                it can't be customised per-newsletter.
              </p>
            </div>
          </details>
        </div>

        {/* ----- RIGHT: preview ----- */}
        <div className="space-y-2 lg:sticky lg:top-2 self-start" data-testid="newsletter-preview-pane">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[11px] uppercase tracking-wider flex items-center gap-1" style={{ color: '#6B7280' }}>
              <Eye size={12} /> Live preview
            </div>
            <div className="flex items-center gap-1">
              {members.length > 0 && (
                <select value={previewMember}
                  onChange={(e) => { setPreviewMember(e.target.value); reloadPreview(); }}
                  className="text-[11px] px-2 py-1 border rounded"
                  style={{ borderColor: '#E5E1DC' }}
                  data-testid="newsletter-personalise-member">
                  <option value="">Generic (no personalisation)</option>
                  {members.map(m => (
                    <option key={m.zoho_contact_id || m.id} value={m.zoho_contact_id || m.id}>
                      {m.name || '—'}
                    </option>
                  ))}
                </select>
              )}
              <button onClick={reloadPreview}
                className="text-[11px] px-2 py-1 border rounded hover:bg-gray-50 inline-flex items-center gap-1"
                style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                data-testid="newsletter-refresh-preview">
                <RotateCcw size={11} /> Refresh
              </button>
            </div>
          </div>
          <div className="rounded-xl border overflow-hidden bg-white"
            style={{ borderColor: '#E5E1DC', height: 'calc(100vh - 220px)' }}>
            <iframe
              ref={previewRef}
              key={previewKey}
              src={previewSrc}
              title="Newsletter preview"
              className="w-full h-full"
              data-testid="newsletter-preview-iframe"
            />
          </div>
          <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
            Save to refresh the preview · public URL: <code>/c/{doc.public_token}</code>
          </p>
        </div>
      </div>

      {showDispatch && (
        <NewsletterDispatchModal
          newsletterId={id}
          clientId={doc.client_id}
          onClose={() => setShowDispatch(false)}
          onDispatched={() => { setShowDispatch(false); refresh(); }}
        />
      )}
    </div>
  );
}

// ----------------------- small UI helpers -----------------------
function FieldText({ label, value, onChange, placeholder, testId }) {
  return (
    <div className="flex-1 min-w-0">
      <label className="text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>{label}</label>
      <input type="text" value={value || ''} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full mt-0.5 px-2 py-1 border rounded text-[13px]" style={{ borderColor: '#E5E1DC' }} />
    </div>
  );
}

function FieldTextarea({ label, value, onChange, rows, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>{label}</label>
      <textarea value={value || ''} rows={rows || 3}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full mt-0.5 px-2 py-1 border rounded text-[13px]" style={{ borderColor: '#E5E1DC' }} />
    </div>
  );
}

function IconBtn({ children, onClick, title, danger, testId }) {
  return (
    <button onClick={onClick} title={title} data-testid={testId}
      className="px-1.5 py-1 border rounded hover:bg-gray-50"
      style={{ borderColor: '#E5E1DC', color: danger ? '#A32D2D' : '#1B2A4A' }}>
      {children}
    </button>
  );
}
